import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Home, Grid3X3, Search, ShoppingCart, User, X, ShoppingBag } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import useCartStore from '../store/cartStore';
import useAuthStore from '../store/authStore';
import axiosInstance from '../api/axios';
import { imgVariant } from '../utils/imgUrl';
import { formatEUR } from '../utils/format';

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSearch, setShowSearch] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [randomProducts, setRandomProducts] = useState([]);
  const [searchSeasonalData, setSearchSeasonalData] = useState(null);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [categories, setCategories] = useState(['Alle Produkte']);

  // Lade Kategorien dynamisch — leere (0 aktive Produkte) werden automatisch ausgeblendet.
  useEffect(() => {
    axiosInstance
      .get('/categories?hide_empty=true')
      .then((r) => {
        const names = (r.data || []).map((c) => (typeof c === 'string' ? c : c.name)).filter(Boolean);
        setCategories(['Alle Produkte', ...names]);
      })
      .catch(() => { /* keep default fallback */ });
  }, []);
  const authUser = useAuthStore(state => state.user);
  const authIsAuthenticated = useAuthStore(state => state.isAuthenticated);

  // Check maintenance mode
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/maintenance`);
        if (response.ok) {
          const data = await response.json();
          const isAdmin = authIsAuthenticated && authUser?.role === 'admin';
          const isAdminPath = window.location.pathname.startsWith('/admin') || window.location.pathname === '/login';
          setIsMaintenanceMode(data.maintenance_mode && !isAdmin && !isAdminPath);
        }
      } catch (error) {
        // Ignore
      }
    };
    checkMaintenance();
  }, [authIsAuthenticated, authUser, location.pathname]);
  
  // Simple and efficient position fixing - removed performance-killing interval and MutationObserver
  useEffect(() => {
    const fixNavPosition = () => {
      const nav = document.querySelector('.mobile-bottom-nav-force-fixed');
      if (nav && window.innerWidth < 1024) {
        // Force position with inline styles to override everything
        nav.style.cssText = `
          position: fixed !important;
          bottom: 0px !important;
          left: 0px !important;
          right: 0px !important;
          z-index: 99999 !important;
          width: 100vw !important;
          transform: translate3d(0, 0, 0) !important;
        `;
      }
    };
    
    // Run immediately and on key events only
    fixNavPosition();
    window.addEventListener('resize', fixNavPosition);
    window.addEventListener('orientationchange', fixNavPosition);
    
    return () => {
      window.removeEventListener('resize', fixNavPosition);
      window.removeEventListener('orientationchange', fixNavPosition);
    };
  }, []);
  
  // Lock body scroll when any modal is open
  useEffect(() => {
    if (showCategories || showSearch || showCart || showAccount) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
  }, [showCategories, showSearch, showCart, showAccount]);
  
  const cartItems = useCartStore(state => state.items);
  const user = useAuthStore(state => state.user);
  
  const cartItemCount = cartItems?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  // Hide on login, register, and admin pages
  const hideOnPages = ['/login', '/register', '/admin'];
  const shouldHide = hideOnPages.some(path => location.pathname.startsWith(path));
  
  if (shouldHide) {
    return null;
  }

  // Categories (from the site) — geladen aus State (siehe useEffect oben, blendet leere Kategorien aus)

  const handleSearchOpen = async () => {
    setShowSearch(true);
    // Load seasonal search config
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seasonal-search/current`);
      if (response.ok) {
        const data = await response.json();
        setSearchSeasonalData(data);
        setRandomProducts(data.products || []);
      } else {
        // Fallback to random products
        const productsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products?limit=6`);
        if (productsResponse.ok) {
          const products = await productsResponse.json();
          const shuffled = products.sort(() => 0.5 - Math.random()).slice(0, 6);
          setRandomProducts(shuffled);
        }
      }
    } catch (error) {
      console.error('Error loading seasonal search:', error);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  const handleCategoryClick = (category) => {
    if (category === 'Alle Produkte') {
      navigate('/products');
    } else {
      navigate(`/products?category=${encodeURIComponent(category)}`);
    }
    setShowCategories(false);
  };

  const handleAccountClick = () => {
    if (user) {
      // Logged in: show account modal with menu options
      setShowAccount(true);
    } else {
      // Not logged in: navigate to account page
      navigate('/account');
    }
  };

  // Account menu options for logged-in users
  const accountMenuOptions = [
    { label: 'Mein Profil', path: '/profile' },
    { label: 'Meine Bestellungen', path: '/my-orders' },
    { label: 'Meine Rechnungen', path: '/my-invoices' },
    { label: 'Meine Coupons', path: '/my-coupons' },
    { label: 'Konto-Einstellungen', path: '/my-account' },
    { label: 'Wunschliste', path: '/wishlist' },
    { label: 'Notizblock', path: '/notepad' },
    { label: 'Kategorien', path: '/categories' },
    { label: 'Blog', path: '/blog' },
  ];

  // Hide navigation during maintenance mode
  if (isMaintenanceMode) {
    return null;
  }

  // Render navigation using portal to ensure it's always at the top level of the DOM
  const navigationContent = (
    <>
      {/* Mobile Bottom Navigation Bar - Only visible on mobile/tablet */}
      <div className="mobile-bottom-nav-force-fixed lg:hidden bg-white border-t border-gray-200 shadow-lg">
        <div className="flex items-stretch justify-between h-14">
          {/* Home — far left */}
          <NavButton
            isActive={location.pathname === '/home'}
            onClick={() => navigate('/home')}
            icon={<Home className="h-[18px] w-[18px]" />}
            label="Home"
            testId="mobile-nav-home"
          />

          {/* Categories */}
          <NavButton
            isActive={location.pathname.startsWith('/categories')}
            onClick={() => navigate('/categories')}
            icon={<Grid3X3 className="h-[18px] w-[18px]" />}
            label="Kategorien"
            testId="mobile-nav-categories"
          />

          {/* Shop — center (landing page lives here) */}
          <NavButton
            isActive={location.pathname === '/' || location.pathname.startsWith('/products')}
            onClick={() => navigate('/')}
            icon={<ShoppingBag className="h-[18px] w-[18px]" />}
            label="Shop"
            testId="mobile-nav-shop"
          />

          {/* Cart */}
          <NavButton
            isActive={false}
            onClick={() => setShowCart(true)}
            icon={<ShoppingCart className="h-[18px] w-[18px]" />}
            label="Warenkorb"
            testId="mobile-nav-cart"
            badge={cartItemCount > 0 ? cartItemCount : null}
          />

          {/* Account */}
          <NavButton
            isActive={location.pathname.startsWith('/account') || location.pathname.startsWith('/mein-konto')}
            onClick={handleAccountClick}
            icon={<User className="h-[18px] w-[18px]" />}
            label="Konto"
            testId="mobile-nav-account"
          />
        </div>
      </div>

      {/* Search Modal */}
      {showSearch && (
        <div 
          className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 z-[100]"
          style={{ 
            position: 'fixed',
            overflow: 'hidden',
            touchAction: 'none'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowSearch(false);
          }}
        >
          <div 
            className="absolute top-1/2 left-1/2 bg-white rounded-lg flex flex-col"
            style={{ 
              transform: 'translate(-50%, -50%)',
              width: 'calc(100vw - 32px)',
              maxWidth: '400px',
              maxHeight: '70vh',
              overflow: 'hidden'
            }}
          >
            <div 
              className="flex-shrink-0 bg-white px-2 py-2 border-b flex items-center gap-1.5"
              style={{ minHeight: '48px' }}
            >
              <Input
                type="text"
                placeholder="Produkte suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                autoFocus
                className="flex-1"
              />
              <button
                onClick={handleSearch}
                className="px-3 py-2 bg-[#D4AF37] hover:bg-[#C9A961] rounded-md flex-shrink-0"
              >
                <Search className="h-3.5 w-3.5 text-white" />
              </button>
              <button
                onClick={() => setShowSearch(false)}
                className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0"
                aria-label="Schließen"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <div 
              className="flex-1 p-2"
              style={{ 
                overflowY: 'auto', 
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y'
              }}
            >
              {/* Seasonal Header */}
              {searchSeasonalData && !searchSeasonalData.is_default && (
                <div className="mb-3 pb-3 border-b border-[#D4AF37]/20">
                  <h2 className="text-base font-bold text-[#8B7355] mb-1 break-words">
                    {searchSeasonalData.headline_text}
                  </h2>
                  <p className="text-xs text-[#6B5744] break-words">
                    {searchSeasonalData.description}
                  </p>
                </div>
              )}
              
              <h3 className="font-semibold text-xs text-[#8B7355] mb-2 break-words">
                {searchSeasonalData && !searchSeasonalData.is_default 
                  ? `${searchSeasonalData.season_name}-Highlights`
                  : 'Vorschläge aus unserem Sortiment'
                }
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {randomProducts.map((product) => (
                  <Card
                    key={product.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => {
                      navigate(`/products/${product.id}`);
                      setShowSearch(false);
                    }}
                  >
                    <div className="aspect-square overflow-hidden rounded-t-lg bg-gray-100">
                      {product.images?.[0] && (
                        <img
                          src={imgVariant(product.images[0], { w: 160 })}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="p-1.5">
                      <p className="text-xs font-medium truncate">{product.name}</p>
                      <p className="text-xs text-[#3E2723] font-bold">{formatEUR(product.price)}</p>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Categories Modal */}
      {showCategories && (
        <div 
          className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 z-[100]"
          style={{ 
            position: 'fixed',
            overflow: 'hidden',
            touchAction: 'none'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCategories(false);
          }}
        >
          <div 
            className="absolute top-1/2 left-1/2 bg-white rounded-lg flex flex-col"
            style={{ 
              transform: 'translate(-50%, -50%)',
              width: 'calc(100vw - 32px)',
              maxWidth: '400px',
              maxHeight: '70vh',
              overflow: 'hidden'
            }}
          >
            <div 
              className="flex-shrink-0 bg-white px-3 py-2.5 border-b flex items-center justify-between"
              style={{ minHeight: '48px' }}
            >
              <h2 className="text-sm font-bold text-[#D4AF37]">Kategorien</h2>
              <button
                onClick={() => setShowCategories(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div 
              className="flex-1 p-2 space-y-1.5"
              style={{ 
                overflowY: 'auto', 
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y'
              }}
            >
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryClick(category)}
                  className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors text-gray-800 border border-gray-200 text-xs"
                  style={{ wordBreak: 'normal', overflowWrap: 'break-word', hyphens: 'none' }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cart Modal */}
      {showCart && (
        <div 
          className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 z-[100]"
          style={{ 
            position: 'fixed',
            overflow: 'hidden',
            touchAction: 'none'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCart(false);
          }}
        >
          <div 
            className="absolute top-1/2 left-1/2 bg-white rounded-lg flex flex-col"
            style={{ 
              transform: 'translate(-50%, -50%)',
              width: 'calc(100vw - 32px)',
              maxWidth: '400px',
              maxHeight: '70vh',
              overflow: 'hidden'
            }}
          >
            <div 
              className="flex-shrink-0 bg-white px-3 py-2.5 border-b flex items-center justify-between"
              style={{ minHeight: '48px' }}
            >
              <h2 className="text-sm font-bold text-[#D4AF37]">Warenkorb</h2>
              <button
                onClick={() => setShowCart(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div 
              className="flex-1 p-2"
              style={{ 
                overflowY: 'auto', 
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y'
              }}
            >
              {cartItemCount === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm mb-3">Ihr Warenkorb ist leer</p>
                  <Button
                    onClick={() => {
                      navigate('/products');
                      setShowCart(false);
                    }}
                    size="sm"
                    className="bg-[#D4AF37] hover:bg-[#C9A961]"
                  >
                    Jetzt shoppen
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {cartItems.map((item) => (
                    <div key={item.product_id} className="flex gap-2 items-center p-2 bg-gray-50 rounded-lg">
                      {item.product?.images?.[0] && (
                        <img src={imgVariant(item.product.images[0], { w: 160 })} alt={item.product.name} className="w-12 h-12 object-cover rounded flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-xs truncate">{item.product?.name}</p>
                        <p className="text-xs text-gray-600">Menge: {item.quantity}</p>
                      </div>
                      <p className="font-bold text-[#3E2723] text-xs flex-shrink-0">{formatEUR((item.price * item.quantity))}</p>
                    </div>
                  ))}
                  <div className="border-t pt-3">
                    <div className="flex justify-between items-center font-bold text-sm mb-3">
                      <span>Gesamt:</span>
                      <span className="text-[#D4AF37]">
                        {formatEUR(cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0))}
                      </span>
                    </div>
                    <Button
                      onClick={() => {
                        navigate('/checkout');
                        setShowCart(false);
                      }}
                      size="sm"
                      className="w-full bg-[#D4AF37] hover:bg-[#C9A961]"
                    >
                      Zur Kasse
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Account Menu Modal - Only for logged-in users */}
      {showAccount && user && (
        <div 
          className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 z-[100]"
          style={{ 
            position: 'fixed',
            overflow: 'hidden',
            touchAction: 'none'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAccount(false);
          }}
        >
          <div 
            className="absolute top-1/2 left-1/2 bg-white rounded-lg flex flex-col"
            style={{ 
              transform: 'translate(-50%, -50%)',
              width: 'calc(100vw - 32px)',
              maxWidth: '400px',
              maxHeight: '80vh',
              overflow: 'hidden'
            }}
          >
            <div 
              className="flex-shrink-0 bg-white px-3 py-2.5 border-b flex items-center justify-between"
              style={{ minHeight: '48px' }}
            >
              <h2 className="text-sm font-bold text-[#D4AF37]">Dein Konto</h2>
              <button
                onClick={() => setShowAccount(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div 
              className="flex-1 p-3"
              style={{ 
                overflowY: 'auto', 
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y'
              }}
            >
              {/* Logged in user info */}
              <div className="mb-3 pb-3 border-b">
                <p className="text-xs text-gray-600">Angemeldet als</p>
                <p className="font-semibold text-sm break-words">{user.first_name} {user.last_name}</p>
                <p className="text-xs text-gray-600 break-all">{user.email}</p>
              </div>

              {/* Menu options for logged in users */}
              <div className="space-y-2">
                {accountMenuOptions.map((option) => (
                  <button
                    key={option.path}
                    onClick={() => {
                      navigate(option.path);
                      setShowAccount(false);
                    }}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-800 text-sm break-words"
                  >
                    {option.label}
                  </button>
                ))}

                {user?.role === 'admin' && (
                  <button
                    onClick={() => {
                      navigate('/admin');
                      setShowAccount(false);
                    }}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors text-[#D4AF37] font-semibold text-sm"
                  >
                    Admin-Bereich
                  </button>
                )}

                <button
                  onClick={() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    localStorage.removeItem('auth-storage');
                    window.location.href = '/';
                  }}
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-red-50 transition-colors text-red-600 text-sm"
                >
                  Abmelden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
  
  // Use React Portal to render navigation at document.body level
  // This ensures position:fixed works correctly even inside transformed containers
  return ReactDOM.createPortal(navigationContent, document.body);
};

export default MobileBottomNav;

/* Unified bottom-nav button — borderless. White background by default,
   active button in dark brown with light text. */
const NavButton = ({ isActive, onClick, icon, label, testId, badge }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-label={label}
    className="flex flex-col items-center justify-center flex-1 min-w-0 h-full relative transition-colors"
    style={{
      backgroundColor: isActive ? '#5D4E3C' : '#FFFFFF',
      color: isActive ? '#C4A084' : '#C9A961',
      fontWeight: isActive ? 600 : 400,
    }}
  >
    {icon}
    {badge != null && (
      <span className="absolute top-0.5 right-0.5 bg-[#D4AF37] text-white text-[9px] rounded-full h-3.5 min-w-[14px] px-0.5 flex items-center justify-center">
        {badge}
      </span>
    )}
    <span className="text-[9px] leading-tight mt-0.5 truncate w-full text-center px-0.5">
      {label}
    </span>
  </button>
);
